/* eslint-disable */
// => https://gitlab.com/gitlab-org/gitlab-ui/-/blob/main/src/components/base/filtered_search/filtered_search_token.vue
// Copyright GitLab B.V.
//
// Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

// This component includes modifications to allow the creation of a token with an empty value.
// This is to enable a filter for an empty field (== "").

import cloneDeep from "lodash/cloneDeep";
const COMMA = ",";
import { createTerm, ensureTokenId, tokenToOption, TOKEN_CLOSE_SELECTOR } from "./filtered_search_utils";
import { GlToken } from "@gitlab/ui";
import { ApckGlFilteredSearchTokenSegment } from "./apck_glfiltered_search_token_segment.js";

const SEGMENT_TITLE = 'TYPE';
const SEGMENT_OPERATOR = 'OPERATOR';
const SEGMENT_DATA = 'DATA';

const DEFAULT_OPERATORS = [
  { value: '=', description: 'is', default: 'true' },
  { value: '!=', description: 'is not' },
];

export const _ApckGlFilteredSearchToken = {
  name: 'GlFilteredSearchToken',
  // FIXME: temporary workaround to ensure compatibility with @vue/compat
  __v_skip: true,

  components: {
    GlToken,
    'gl-filtered-search-token-segment': ApckGlFilteredSearchTokenSegment,
  },
  inheritAttrs: false,
  props: {
    availableTokens: {
      type: Array,
      required: false,
      default: () => [],
    },
    /**
     * Token configuration with available operators and options.
     */
    config: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    /**
     * Determines if the token is being edited or not.
     */
    active: {
      type: Boolean,
      required: false,
      default: false,
    },
    multiSelectValues: {
      type: Array,
      required: false,
      default: () => [],
    },
    /**
     * Current token value.
     */
    value: {
      type: Object,
      required: false,
      default: () => ({ operator: '', data: '' }),
    },
    /**
     * Display operators' descriptions instead of their values (e.g., "is" instead of "=").
     */
    showFriendlyText: {
      type: Boolean,
      required: false,
      default: false,
    },
    placeholder: {
      type: String,
      required: false,
      default: '',
    },
    cursorPosition: {
      type: String,
      required: false,
      default: 'end',
      validator: (value) => ['start', 'end'].includes(value),
    },
    viewOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
    dataSegmentInputAttributes: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  data() {
    return {
      activeSegment: null,
      tokenValue: cloneDeep(this.value),
      intendedCursorPosition: this.cursorPosition,
    };
  },

  computed: {
    operators() {
      return this.config.operators || DEFAULT_OPERATORS;
    },

    hasDataOrDataSegmentIsCurrentlyActive() {
      return this.tokenValue.data !== '' || this.isSegmentActive(SEGMENT_DATA);
    },

    availableTokensWithSelf() {
      return [this.config, ...this.availableTokens.filter((token) => token !== this.config)].map(
        tokenToOption
      );
    },

    operatorDescription() {
      const operator = this.operators.find((op) => op.value === this.tokenValue.operator);
      return (this.showFriendlyText || this.config?.showFriendlyText) ? operator?.description : operator?.value;
    },

    eventListeners() {
      return this.viewOnly ? {} : { mousedown: this.destroyByClose };
    },
  },
  segments: {
    SEGMENT_TITLE,
    SEGMENT_DATA,
    SEGMENT_OPERATOR,
  },
  watch: {
    tokenValue: {
      deep: true,
      handler(newValue) {
        /**
         * Emitted when the token changes its value.
         *
         * @event input
         * @type {object} dataObj Object containing the update value.
         */
        this.$emit('input', newValue);
      },
    },

    value: {
      handler(newValue, oldValue) {
        if (newValue?.data === oldValue?.data && newValue?.operator === oldValue?.operator) {
          return;
        }

        this.tokenValue = cloneDeep(newValue);
      },
    },

    active: {
      immediate: true,
      handler(newValue) {
        // https://dev.appcheck-ng.com/appcheck-ng/apck-vuln-feed/-/issues/330
        if (this.activeSegment === SEGMENT_OPERATOR && this.tokenValue.data === '') {
          this.activateSegment(SEGMENT_OPERATOR);
          /**
          * To get around this we could call the activateSegment explicitly for the OPERATOR
          * segment.
          * Returning early here will break tests as the token no longers correctly emits `destroy()`
          * when empty.
          * return;
          */
        }
        if (newValue) {
          this.intendedCursorPosition = this.cursorPosition;
          if (!this.activeSegment) {
            this.activateSegment(this.tokenValue.data !== '' ? SEGMENT_DATA : SEGMENT_OPERATOR);
          }
        } else if (this.tokenValue.data === '' && this.activeSegment === SEGMENT_OPERATOR) {
          /**
          * This is a very specific match for when the user has deleted the whole DATA segment input
          * and the OPERATOR segment is supposed to become active.
          * In Firefox this handler is not called after a Backspace event
          * (and the handler there correctly activates the OPERATOR segment).
          * In Chromium-based browsers this handler is entered instead, and the whole token
          * is destroyed.
          * In this case we want to reactivate the OPERATOR segment
          * since all data's been deleted and we want to allow the user
          * to re-choose the operator.
          */
          this.activateSegment(SEGMENT_OPERATOR);
        } else if (this.tokenValue.data === '') {
          this.activeSegment = null;
          /**
           * Emitted when token is about to be destroyed.
           *
           * @event destroy
           */
          this.$emit('destroy');
        }
      },
    },
  },

  created() {
    if (!('operator' in this.tokenValue)) {
      if (this.operators.length === 1) {
        const operator = this.operators[0].value;
        this.$emit('input', { ...this.tokenValue, operator });
        this.activeSegment = SEGMENT_DATA;
      } else {
        this.$emit('input', { ...this.tokenValue, operator: '' });
      }
    }
  },

  methods: {
        displayError(errorMsg) {
      // pipe this event upwards
      this.$emit("displayError", errorMsg);
    },
    activateSegment(segment) {
      if (this.viewOnly) return;

      if (this.activeSegment !== segment) this.activeSegment = segment;

      if (!this.active) {
        /**
         * Emitted when this term token is clicked.
         *
         * @event activate
         */
        this.$emit('activate');
      }
    },

    getAdditionalSegmentClasses(segment) {
      if (this.viewOnly) {
        return 'gl-cursor-text';
      }
      return { 'gl-cursor-pointer': !this.isSegmentActive(segment) };
    },

    isSegmentActive(segment) {
      return this.active && this.activeSegment === segment;
    },

    replaceWithTermIfEmpty() {
      if (this.tokenValue.operator === '' && this.tokenValue.data === '') {
        /**
         * Emitted when this token is converted to another type
         * @property {object} token Replacement token configuration
         */
        this.$emit('replace', createTerm(this.config.title));
      }
    },

    replaceToken(newType) {
      const newTokenConfig = this.availableTokens.find(({ type }) => type === newType);

      if (newTokenConfig === this.config) {
        this.$nextTick(() => {
          /**
           * Emitted when this term token will lose its focus.
           *
           * @event deactivate
           */
          this.$emit('deactivate');
        });
        return;
      }

      if (newTokenConfig) {
        const isCompatible =
          this.config.dataType && this.config.dataType === newTokenConfig.dataType;
        this.$emit('replace', {
          type: newTokenConfig.type,
          value: isCompatible ? this.tokenValue : { data: '' },
        });
      }
    },

    handleOperatorKeydown(evt, { inputValue, suggestedValue, applySuggestion }) {
      const { key } = evt;
      if (key === ' ' || key === 'Spacebar') {
        applySuggestion(suggestedValue);
        return;
      }

      const potentialValue = `${inputValue}${key}`;
      if (
        key.length === 1 &&
        !this.operators.find(({ value }) => value.startsWith(potentialValue))
      ) {
        if (this.tokenValue.data === '') {
          applySuggestion(suggestedValue);
        } else {
          evt.preventDefault();
        }
      }
    },

    activateDataSegment() {
      if (this.config.multiSelect) {
        this.$emit('input', { ...this.tokenValue, data: '' });
      }
      this.activateSegment(this.$options.segments.SEGMENT_DATA);
    },

    activatePreviousOperatorSegment() {
      this.activateSegment(this.$options.segments.SEGMENT_OPERATOR);
      this.intendedCursorPosition = 'end';
    },

    activatePreviousTitleSegment() {
      this.activateSegment(this.$options.segments.SEGMENT_TITLE);
      this.intendedCursorPosition = 'end';
    },

    activateNextDataSegment() {
      this.activateDataSegment();
      this.intendedCursorPosition = 'start';
    },

    activateNextOperatorSegment() {
      this.activateSegment(this.$options.segments.SEGMENT_OPERATOR);
      this.intendedCursorPosition = 'start';
    },

    handleComplete() {
      if (this.config.multiSelect) {
        this.$emit('input', { ...this.tokenValue, data: this.multiSelectValues.join(COMMA) });
      }
      /**
       * Emitted when the token entry has been completed.
       *
       * @event complete
       */
      this.$emit('complete');
    },

    destroyByClose(event) {
      if (event.target.closest(TOKEN_CLOSE_SELECTOR)) {
        event.preventDefault();
        event.stopPropagation();
        this.$emit('destroy');
      }
    },
  },
  template: `
  <div
    class="gl-filtered-search-token"
    :class="{
      'gl-filtered-search-token-active': active,
      'gl-filtered-search-token-hover': !viewOnly,
      'gl-cursor-default': viewOnly,
    }"
    data-testid="filtered-search-token"
  >
    <!--
      Emitted when the token is submitted.
      @event submit
    -->

    <gl-filtered-search-token-segment
      key="title-segment"
      :value="config.title"
      :active="isSegmentActive($options.segments.SEGMENT_TITLE)"
      :cursor-position="intendedCursorPosition"
      :options="availableTokensWithSelf"
      :view-only="viewOnly"
      @displayError="displayError"
      @activate="activateSegment($options.segments.SEGMENT_TITLE)"
      @deactivate="$emit('deactivate')"
      @complete="replaceToken"
      @backspace="$emit('destroy')"
      @submit="$emit('submit')"
      @previous="$emit('previous')"
      @next="activateNextOperatorSegment"
    >
      <template #view="{ inputValue }">
        <gl-token
          class="gl-filtered-search-token-type"
          :class="getAdditionalSegmentClasses($options.segments.SEGMENT_TITLE)"
          view-only
        >
          {{ inputValue }}
        </gl-token>
      </template>
    </gl-filtered-search-token-segment>

    <gl-filtered-search-token-segment
      key="operator-segment"
      v-model="tokenValue.operator"
      :active="isSegmentActive($options.segments.SEGMENT_OPERATOR)"
      :cursor-position="intendedCursorPosition"
      :options="operators"
      option-text-field="value"
      :custom-input-keydown-handler="handleOperatorKeydown"
      :view-only="viewOnly"
      @displayError="displayError"  
      @activate="activateSegment($options.segments.SEGMENT_OPERATOR)"
      @backspace="replaceWithTermIfEmpty"
      @complete="activateSegment($options.segments.SEGMENT_DATA)"
      @deactivate="$emit('deactivate')"
      @previous="activatePreviousTitleSegment"
      @next="activateNextDataSegment"
    >

      <template #view>
        <gl-token
          class="gl-filtered-search-token-operator"
          variant="search-value"
          :class="getAdditionalSegmentClasses($options.segments.SEGMENT_OPERATOR)"
          view-only
        >
          {{ operatorDescription }}
        </gl-token>
      </template>

      <template #option="{ option }">
        <div class="gl-display-flex">
          {{ showFriendlyText ? option.description : option.value }}
          <span v-if="option.description" class="gl-filtered-search-token-operator-description">
            {{ showFriendlyText ? option.value : option.description }}
          </span>
        </div>
      </template>
    </gl-filtered-search-token-segment>

    <!--
      Emitted when a suggestion has been selected.
      @event select
      @type {string} value The value of the selected suggestion.
    -->

    <!--
      Emitted when Space is pressed in-between term text.
      Not emitted when termsAsTokens is true.
      @event split
      @property {array} newTokens Token configurations
    -->

    <gl-filtered-search-token-segment
      v-if="hasDataOrDataSegmentIsCurrentlyActive"
      key="data-segment"
      v-model="tokenValue.data"
      :active="isSegmentActive($options.segments.SEGMENT_DATA)"
      :cursor-position="intendedCursorPosition"
      :multi-select="config.multiSelect"
      :options="config.options"
      :view-only="viewOnly"
      :inputType="config?.inputType"
      :placeholder="placeholder"
      @displayError="displayError"
      :search-input-attributes="dataSegmentInputAttributes"
      @activate="activateDataSegment"
      @backspace="activateSegment($options.segments.SEGMENT_OPERATOR)"
      @complete="handleComplete"
      @select="$emit('select', $event)"
      @submit="$emit('submit')"
      @deactivate="$emit('deactivate')"
      @split="$emit('split', $event)"
      @previous="activatePreviousOperatorSegment"
      @next="$emit('next')"
    >
      <template #before-input="scope">
        <slot name="before-data-segment-input" v-bind="scope"></slot>
      </template>

      <template #suggestions>
        <!-- @slot The suggestions (implemented with GlFilteredSearchSuggestion). -->

        <slot name="suggestions"></slot>
      </template>

      <template #view="{ inputValue }">
        <!-- @slot Used to customize how the token is rendered. -->

        <slot
          name="view-token"
          v-bind="{
            inputValue,
            listeners: eventListeners,
            cssClasses: {
              'gl-filtered-search-token-data': true,
              ...getAdditionalSegmentClasses($options.segments.SEGMENT_DATA),
            },
          }"
        >
          <gl-token
            class="gl-filtered-search-token-data"
            variant="search-value"
            :class="getAdditionalSegmentClasses($options.segments.SEGMENT_DATA)"
            :view-only="viewOnly"
            v-on="eventListeners"
          >
            <span class="gl-filtered-search-token-data-content">
              <!--
              @slot Template for token value in inactive state
              @binding {array} suggestions Slot for rendering autocomplete suggestions when no options are provided.
              -->

              <slot name="view" v-bind="{ inputValue }">{{ inputValue }}</slot>
            </span>
          </gl-token>
        </slot>
      </template>
    </gl-filtered-search-token-segment>
  </div>

  `,
};
export const ApckGlFilteredSearchToken = ensureTokenId(
  _ApckGlFilteredSearchToken
);
